import React from "react";
import { Link } from "react-router-dom";
import DefaultHeader from "../../container/default-layout/header";
import DefaultSideBar from "../../container/default-layout/side.bar";
import { DefaultFooter } from "../../container/default-layout/footer";
import SimpleReactValidator from "simple-react-validator";
import AppService from "../../../services/app.service";
import Loader from "../../../components/loader";
import Select from "react-select";
import { CustomDatePicker } from "../../../components/CustomDatePicker";
import TipTapEditor from "../../../components/TipTapEditor";
import {
  containsLocalMedia,
  withUploadedMedia,
} from "../../../services/SsClassMediaService";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import * as vendorRules from "../../../utils/self.serve.vendor.rules";
import { selectAllSimulations } from "../../../utils/utils";
import DateUtils from "../../../utils/dateUtils";

/**
 * Self-Serve Class add/edit (ELS-595 / ELS-639, access codes ELS-640).
 *
 * Routes: `/classes/self-serve/add`, `/classes/self-serve/edit/:id`
 * APIs: `POST|PUT|GET /api/ss-classes`, access-code inventory/generate under same resource.
 *
 * Vendor rules (UI + API): Student Pay alone, or VitalSource and/or Follett.
 * Bookstore edit mode: one Generate button opens a popup to pick one or more vendors, each
 * with its own quantity; success downloads one `.xls` with a sheet per vendor
 * (`{ClassName}-{Vendors}.xls`). "Download all generated codes" is the same workbook shape
 * for every bookstore code on the class (Student Pay excluded); it only shows once a bookstore
 * code exists. Available counts are shown per saved vendor.
 * Landing slug/URL are server-generated; public page is `/class/{slug}`.
 *
 * @see docs/edsims/modules/21-self-serve-enrollment.md
 */

class AddSelfServeClass extends React.Component<any, any> {
  state: any = {
    id: null,
    name: "",
    description: "",
    comments: "",
    accountId: null,
    accountsList: [],
    vendors: [],
    /** Bookstore/Student Pay vendor keys last persisted for this class (Generate uses these). */
    savedVendors: [] as string[],
    expiryDate: "",
    expiryDateInvalid: false,
    accessWindowDays: 90,
    paymentAmount: "",
    landingSlug: "",
    landingUrl: "",
    landingInstructions: "",
    /**
     * The editor seeds itself from `content` on mount and ignores later changes, so on the edit
     * route it must not mount until the class has been fetched. Gating on the instructions being
     * non-empty (as the message centre does) would never mount it for a class that has none.
     */
    editorReady: !this.props.match?.params?.id,
    landingOptionPay: true,
    landingOptionEnterCode: true,
    landingOptionForgotCode: true,
    /** Same flag as Standard Classes (`classes.active`). Default on for create. */
    active: true,
    simulationsList: [],
    simulationIds: [],
    loading: false,
    hasError: false,
    errorMsg: "",
    showVendorWarningModal: false,
    vendorWarningMessage: "",
    /**
     * Selection to restore if the admin cancels a removal, or `null` when the warning is
     * purely informational (a rule the server rejected) and there is nothing to put back.
     */
    vendorSelectionUndo: null as any,
    /** Saved vendors dropped in this editing session (codes revoke on save). */
    pendingRevokeVendors: [] as string[],
    isEdit: false,
    copyFeedback: "",
    codeQuantity: 25,
    showGenerateModal: false,
    generateModalVendors: [] as { value: string; label: string }[],
    /** Quantity typed per vendor key — each vendor mints its own amount. */
    generateModalQuantities: {} as Record<string, string>,
    generateModalQuantityErrors: {} as Record<string, string>,
    generateModalVendorError: "",
    generateModalError: "",
    /** Per-vendor inventory: { [vendorKey]: { available, redeemed, expired, revoked } } */
    codeInventoryByVendor: {} as Record<string, vendorRules.VendorInventory>,
  };
  validator: any;

  componentWillMount = () => {
    this.validator = new SimpleReactValidator({
      messages: { default: "Field is required!" },
    });
    this.loadAccounts();
    const editId = this.props.match?.params?.id;
    if (editId) {
      this.loadClass(Number(editId));
    }
  };

  loadAccounts = async () => {
    this.setState({ loading: true });
    const accounts = await AppService.getAll("accounts/activeAccounts");
    this.setState({
      accountsList: (accounts.data || []).map((account) => ({
        value: account.accId,
        label: account.accName,
      })),
      loading: false,
    });
  };

  /** Same account-scoped dropdown as Add Student: `GET /sims/list/{accountId}`. */
  loadSimulations = async (accountId, selectAll = false) => {
    this.setState({ loading: true });
    const sims = await AppService.getAllWithData("sims/list", accountId);
    const list = (sims.data || []).map((s) => ({
      value: s.value,
      label: s.label,
    }));
    this.setState({
      simulationsList: list,
      ...(selectAll ? { simulationIds: [...list] } : {}),
      loading: false,
    });
    return list;
  };

  loadClass = async (id: number) => {
    this.setState({ loading: true });
    try {
      const c = await AppService.get(id, "ss-classes");
      const accountSims = await this.loadSimulations(c.accountId);
      const byId = new Map(accountSims.map((s) => [Number(s.value), s]));
      const vendorValues = (c.vendors || []).map((code: string) =>
        vendorRules.VENDOR_OPTIONS.find((v) => v.value === code)
      ).filter(Boolean);
      const vendorCodes = (c.vendors || []) as string[];
      this.setState({
        isEdit: true,
        id,
        name: c.name || "",
        description: c.description || "",
        comments: c.comments || "",
        accountId: c.accountId
          ? { value: c.accountId, label: c.accountName }
          : null,
        vendors: vendorValues,
        savedVendors: vendorCodes,
        expiryDate: c.expiryDate || "",
        expiryDateInvalid: false,
        accessWindowDays: c.accessWindowDays || 90,
        paymentAmount: c.paymentAmount ?? "",
        landingSlug: c.landingSlug || "",
        landingUrl: c.landingUrl || "",
        landingInstructions: c.landingInstructions || "",
        landingOptionPay: !!c.landingOptionPay,
        landingOptionEnterCode: !!c.landingOptionEnterCode,
        landingOptionForgotCode: !!c.landingOptionForgotCode,
        active: this.isExpiryOnOrBeforeToday(c.expiryDate)
          ? false
          : c.active !== false,
        simulationIds: (c.simulationIds || [])
          .map((simId) => byId.get(Number(simId)))
          .filter(Boolean),
        loading: false,
        editorReady: true,
      });
      await this.loadAccessCodeInventory(id);
    } catch (e) {
      this.setState({ loading: false, isEdit: false, id: null, editorReady: false });
      this.props.history.push("/classes");
    }
  };

  loadAccessCodeInventory = async (classId?: number) => {
    const id = classId ?? this.state.id;
    if (!id) {
      return;
    }
    try {
      const res = await AppService.getAll(
        `ss-classes/${id}/access-codes/inventory`
      );
      const data = res.data || {};
      const byVendor: Record<string, vendorRules.VendorInventory> = {};
      (data.byVendor || []).forEach((row: any) => {
        if (!row?.vendorKey) {
          return;
        }
        byVendor[row.vendorKey] = {
          available: row.available ?? 0,
          redeemed: row.redeemed ?? 0,
          expired: row.expired ?? 0,
          revoked: row.revoked ?? 0,
        };
      });
      this.setState({ codeInventoryByVendor: byVendor });
    } catch (e) {
      // Inventory is non-blocking for edit form load.
    }
  };

  /** Vendors already saved on the class — Generate is only valid for these. */
  savedBookstoreVendorKeys = (): string[] =>
    vendorRules.savedBookstoreVendorKeys(this.state.savedVendors);

  /**
   * Saved bookstore vendors still selected on the form. Removing one drops it from
   * Generate immediately, before the class is saved.
   */
  bookstoreVendorsForGenerate = (): string[] => {
    const selected = (this.state.vendors || []).map((v) => v.value);
    return this.savedBookstoreVendorKeys().filter(
      (key) => selected.indexOf(key) >= 0
    );
  };

  /** Bookstore vendors selected in the form but not yet saved. */
  unsavedBookstoreVendorKeys = (): string[] =>
    vendorRules.unsavedBookstoreVendorKeys(
      this.state.savedVendors,
      (this.state.vendors || []).map((v) => v.value)
    );

  bookstoreVendorsForGenerateOptions = () =>
    this.bookstoreVendorsForGenerate().map((key) => ({
      value: key,
      label: vendorRules.vendorLabel(key),
    }));

  vendorLabel = (vendorKey: string) => vendorRules.vendorLabel(vendorKey);

  vendorInventory = (vendorKey: string): vendorRules.VendorInventory =>
    vendorRules.vendorInventory(this.state.codeInventoryByVendor, vendorKey);

  triggerFileDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * True once the class has a bookstore code in any state, so "Download all" has something to
   * export. Student Pay codes are never exported.
   */
  hasBookstoreCodes = (): boolean =>
    Object.entries(this.state.codeInventoryByVendor || {}).some(
      ([vendorKey, inv]: [string, any]) =>
        vendorRules.isBookstoreVendor(vendorKey) &&
        Number(inv?.available || 0) +
          Number(inv?.redeemed || 0) +
          Number(inv?.expired || 0) +
          Number(inv?.revoked || 0) >
        0
    );

  openGenerateModal = () => {
    const saved = this.bookstoreVendorsForGenerate();
    if (!saved.length) {
      return;
    }
    const quantity = String(this.state.codeQuantity || 25);
    const quantities: Record<string, string> = {};
    saved.forEach((key) => {
      quantities[key] = quantity;
    });
    this.setState({
      showGenerateModal: true,
      generateModalVendors: this.bookstoreVendorsForGenerateOptions(),
      generateModalQuantities: quantities,
      generateModalQuantityErrors: {},
      generateModalVendorError: "",
      generateModalError: "",
      hasError: false,
      errorMsg: "",
    });
  };

  closeGenerateModal = () => {
    this.setState({
      showGenerateModal: false,
      generateModalQuantityErrors: {},
      generateModalVendorError: "",
      generateModalError: "",
    });
  };

  handleGenerateVendorsChange = (options) => {
    const selected = options || [];
    const quantities = { ...(this.state.generateModalQuantities || {}) };
    selected.forEach((option) => {
      if (quantities[option.value] === undefined) {
        quantities[option.value] = String(this.state.codeQuantity || 25);
      }
    });
    this.setState({
      generateModalVendors: selected,
      generateModalQuantities: quantities,
      generateModalVendorError: "",
      generateModalError: "",
    });
  };

  handleGenerateQuantityChange = (vendorKey: string, raw: string) => {
    if (!vendorRules.isAcceptableQuantityInput(raw)) {
      return;
    }
    this.setState({
      generateModalQuantities: {
        ...this.state.generateModalQuantities,
        [vendorKey]: raw,
      },
      generateModalQuantityErrors: {
        ...this.state.generateModalQuantityErrors,
        [vendorKey]: "",
      },
      generateModalError: "",
    });
  };

  confirmGenerateFromModal = () => {
    const vendors = this.state.generateModalVendors || [];
    const vendorError = vendors.length ? "" : "The vendor field is required.";
    const quantityErrors: Record<string, string> = {};
    const items = vendors.map((option) => {
      const raw = this.state.generateModalQuantities[option.value] ?? "";
      const validation = vendorRules.validateGenerateRequest(option.value, raw);
      if (validation.quantityError) {
        quantityErrors[option.value] = validation.quantityError;
      }
      return { vendorKey: option.value, quantity: Number(raw) };
    });
    if (vendorError || Object.keys(quantityErrors).length) {
      this.setState({
        generateModalVendorError: vendorError,
        generateModalQuantityErrors: quantityErrors,
        generateModalError: "",
      });
      return;
    }
    this.generateCodes(items);
  };

  generateCodes = async (items: { vendorKey: string; quantity: number }[]) => {
    if (!this.state.id) {
      this.setState({
        generateModalError: "Save the class before generating access codes.",
      });
      return;
    }
    this.setState({
      loading: true,
      hasError: false,
      generateModalError: "",
    });
    try {
      // One request for every vendor: the server mints them together (all or nothing).
      const res = await AppService.downloadFromStream(
        { items },
        `ss-classes/${this.state.id}/access-codes/generate`,
        "post",
        "blob",
        true
      );
      this.triggerFileDownload(
        res.data,
        vendorRules.codesFilename(
          this.state.name,
          items.map((item) => item.vendorKey)
        )
      );
      this.setState({
        loading: false,
        showGenerateModal: false,
        codeQuantity: items[items.length - 1]?.quantity || this.state.codeQuantity,
        generateModalQuantityErrors: {},
        generateModalVendorError: "",
        generateModalError: "",
      });
      this.loadAccessCodeInventory();
    } catch (err: any) {
      const message = await vendorRules.apiErrorMessage(err, "Unable to generate codes.");
      this.setState({ loading: false, generateModalError: message });
    }
  };

  downloadAllCodes = async () => {
    if (!this.state.id) {
      this.setState({
        hasError: true,
        errorMsg: "Save the class before downloading access codes.",
      });
      return;
    }
    this.setState({ loading: true, hasError: false });
    try {
      const res = await AppService.downloadFromStream(
        {},
        `ss-classes/${this.state.id}/access-codes/export`,
        "post",
        "blob",
        true
      );
      this.setState({ loading: false });
      this.triggerFileDownload(res.data, vendorRules.codesFilename(this.state.name));
    } catch (err: any) {
      const message = await vendorRules.apiErrorMessage(err, "Unable to download access codes.");
      this.setState({ loading: false, hasError: true, errorMsg: message });
    }
  };

  handleChange = (event: { target: { name: any; value: any; type?: string; checked?: boolean } }) => {
    const { name, value, type, checked } = event.target;
    if (name === "paymentAmount") {
      const raw = String(value);
      if (raw !== "" && !/^\d{0,8}(\.\d{0,2})?$/.test(raw)) {
        return;
      }
    }
    if (name === "accessWindowDays") {
      const raw = String(value);
      if (raw !== "" && !/^\d{1,9}$/.test(raw)) {
        return;
      }
    }
    this.setState({ [name]: type === "checkbox" ? checked : value });
  };

  /** Same lexicographic compare as expireClasses / DateUtils.formatDate (yyyy-MM-dd). */
  isExpiryOnOrBeforeToday = (expiryDate?: string) => {
    const date = expiryDate || "";
    return !!date && date <= DateUtils.formatDate(new Date());
  };

  handleDateChange = (expiry) => {
    if (expiry && expiry.isValid) {
      const date = expiry.date || "";
      const expired = this.isExpiryOnOrBeforeToday(date);
      this.setState(
        {
          expiryDate: date,
          expiryDateInvalid: false,
          active: expired ? false : true,
        },
        () => {
          this.validator.hideMessageFor("expiryDate");
          this.forceUpdate();
        }
      );
      return;
    }
    this.setState({ expiryDateInvalid: true }, () => {
      this.validator.hideMessageFor("expiryDate");
      this.forceUpdate();
    });
  };

  handleAccountChange = (option) => {
    if (this.state.isEdit) {
      return;
    }
    this.setState({
      accountId: option,
      simulationIds: [],
      simulationsList: [],
    });
    if (option?.value) {
      this.loadSimulations(option.value, true);
    }
  };

  handleSimulationChange = (newValue, actionMeta) => {
    const accountSims = this.state.simulationsList || [];
    const { action, option } = actionMeta || {};
    if (action === "select-option" && option?.value === selectAllSimulations.value) {
      this.setState({ simulationIds: [...accountSims] });
      return;
    }
    const allowed = new Set(accountSims.map((s) => Number(s.value)));
    const next = (newValue || []).filter(
      (s) =>
        s.value !== selectAllSimulations.value && allowed.has(Number(s.value))
    );
    this.setState({ simulationIds: next });
  };

  getSimulationOptions = () => {
    const list = this.state.simulationsList || [];
    if (!list.length) {
      return list;
    }
    if ((this.state.simulationIds || []).length === list.length) {
      return [...list];
    }
    return [selectAllSimulations, ...list];
  };

  getVendorOptions = () =>
    vendorRules.vendorOptionsForSelection(
      (this.state.vendors || []).map((v) => v.value)
    );

  handleVendorChange = (options) => {
    const selected = options || [];
    const result = vendorRules.resolveVendorSelection({
      previousCodes: (this.state.vendors || []).map((o) => o.value),
      nextCodes: selected.map((o) => o.value),
      savedVendors: this.state.savedVendors || [],
      inventoryByVendor: this.state.codeInventoryByVendor || {},
      acknowledgedRevokeVendors: this.state.pendingRevokeVendors || [],
      landingOptions: {
        landingOptionPay: !!this.state.landingOptionPay,
        landingOptionEnterCode: !!this.state.landingOptionEnterCode,
        landingOptionForgotCode: !!this.state.landingOptionForgotCode,
      },
    });

    // Mixed Student Pay + bookstore cannot be chosen in the dropdown; ignore if it arrives.
    if (result.blocked) {
      return;
    }

    this.setState({
      vendors: selected,
      ...result.landingOptions,
      pendingRevokeVendors: result.pendingRevokeVendors,
      ...(result.warning && this.state.isEdit
        ? {
            showVendorWarningModal: true,
            vendorWarningMessage: result.warning,
            // Captured before the change lands, so Cancel can put the vendor back along
            // with the landing options its removal just rewrote.
            vendorSelectionUndo: {
              vendors: this.state.vendors || [],
              landingOptionPay: this.state.landingOptionPay,
              landingOptionEnterCode: this.state.landingOptionEnterCode,
              landingOptionForgotCode: this.state.landingOptionForgotCode,
              pendingRevokeVendors: this.state.pendingRevokeVendors || [],
            },
          }
        : {}),
    });
  };

  /** Closes the warning. The removal stays staged for save and is not asked again. */
  acceptVendorWarning = () => {
    this.setState({ showVendorWarningModal: false });
  };

  /** Puts the removed vendor back, so no codes are staged for revocation. */
  cancelVendorChange = () => {
    this.setState({
      ...(this.state.vendorSelectionUndo || {}),
      showVendorWarningModal: false,
    });
  };

  /**
   * Deferred to the end of the close transition. The dialog stays mounted while it fades out,
   * so clearing the message and the undo snapshot on click would blank the text and swap the
   * buttons in full view.
   */
  clearVendorWarning = () => {
    this.setState({ vendorWarningMessage: "", vendorSelectionUndo: null });
  };

  /**
   * Backdrop and Escape revert rather than accept: dismissing a warning by accident should not
   * leave codes queued for revocation.
   */
  closeVendorWarningModal = () => {
    if (this.state.vendorSelectionUndo) {
      this.cancelVendorChange();
      return;
    }
    this.acceptVendorWarning();
  };

  isStudentPay = () =>
    (this.state.vendors || []).some((v) => v.value === "STUDENT_PAY");

  isBookstore = () => {
    const vendors = this.state.vendors || [];
    return vendors.length > 0 && !this.isStudentPay();
  };

  buildPayload = () => {
    const isPay = this.isStudentPay();
    return {
      name: this.state.name,
      description: this.state.description,
      comments: this.state.comments,
      accountId: this.state.accountId?.value,
      vendors: (this.state.vendors || []).map((v) => v.value),
      expiryDate: isPay ? this.state.expiryDate : null,
      accessWindowDays: isPay ? null : Number(this.state.accessWindowDays),
      paymentAmount: isPay ? Number(this.state.paymentAmount) : null,
      landingInstructions: this.state.landingInstructions,
      landingOptionPay: !!this.state.landingOptionPay,
      landingOptionEnterCode: !!this.state.landingOptionEnterCode,
      landingOptionForgotCode: !!this.state.landingOptionForgotCode,
      simulationIds: (this.state.simulationIds || [])
        .filter((s) => s.value !== selectAllSimulations.value)
        .map((s) => s.value),
      active: !!this.state.active,
    };
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    // Student Pay and bookstore fields stay in the validator after they unmount.
    const hiddenFields = this.isStudentPay()
      ? ["accessWindowDays"]
      : ["expiryDate", "paymentAmount"];
    hiddenFields.forEach((field) => {
      delete this.validator.fields[field];
      delete this.validator.errorMessages[field];
    });
    if (!this.validator.allValid() || (this.isStudentPay() && this.state.expiryDateInvalid)) {
      this.validator.showMessages();
      this.forceUpdate();
      return;
    }
    this.saveClass();
  };

  saveClass = async () => {
    this.setState({ loading: true, hasError: false });
    try {
      // Instruction media has to reach S3 first: base64 in the payload would overflow the
      // TEXT column, and a truncated write leaves unreadable markup behind.
      const landingInstructions = await withUploadedMedia(
        this.state.landingInstructions
      );
      if (containsLocalMedia(landingInstructions)) {
        this.setState({
          loading: false,
          hasError: true,
          errorMsg:
            "Landing page pictures or videos could not be uploaded. Check your connection and save again.",
        });
        return;
      }
      const payload = { ...this.buildPayload(), landingInstructions };
      let res;
      if (this.state.isEdit) {
        res = await AppService.update(payload, "ss-classes", this.state.id);
      } else {
        res = await AppService.submit(payload, "ss-classes");
      }
      if (res.status === 200 || res.status === 201) {
        // A vendor removal was already confirmed in the warning dialog; no second popup on save.
        this.props.history.push("/classes");
        return;
      }
    } catch (err: any) {
      const msg = await vendorRules.apiErrorMessage(err, "Unable to save self-serve class.");
      // Vendor-combination rejections belong on the Vendors field, not the page-level alert.
      const isVendorRule =
        typeof msg === "string" &&
        msg.toLowerCase().includes("vendor") &&
        err?.response?.status === 400;
      if (isVendorRule) {
        this.setState({
          showVendorWarningModal: true,
          vendorWarningMessage: msg,
          // A server-side rejection is not a local change, so there is nothing to cancel.
          vendorSelectionUndo: null,
          hasError: false,
          errorMsg: "",
        });
      } else {
        this.setState({
          hasError: true,
          errorMsg: msg,
        });
      }
    }
    this.setState({ loading: false });
  };

  copyLandingUrl = async () => {
    if (!this.state.landingUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(this.state.landingUrl);
      this.setState({ copyFeedback: "Copied!" });
      setTimeout(() => this.setState({ copyFeedback: "" }), 2000);
    } catch (e) {
      this.setState({ copyFeedback: "Copy failed" });
    }
  };

  render() {
    const isPay = this.isStudentPay();
    const isBookstore = this.isBookstore();

    return (
      <div>
        <div className="page">
          <DefaultHeader history={this.props.history} />
          <div className="page-content d-flex align-items-stretch fields-font">
            <DefaultSideBar activeClassLink={true} />
            <div className="content-inner">
              <header className="page-header">
                <div className="container-fluid">
                  <h2 className="heading">Class Management</h2>
                </div>
              </header>
              <div className="breadcrumb-holder container-fluid">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item breadcrumb-list">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item breadcrumb-list">
                    <Link to="/classes">Class Management</Link>
                  </li>
                  <li className="breadcrumb-item active breadcrumb-list">
                    {this.state.isEdit ? "Edit" : "Add"} Self-Serve Class
                  </li>
                </ul>
              </div>
              <section className="forms">
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="card">
                        <div className="card-header d-flex align-items-center">
                          <h3 className="sub-heading">
                            {this.state.isEdit ? "Edit" : "Add"} Self-Serve Class
                          </h3>
                        </div>
                        <div className="card-body">
                          {this.state.hasError ? (
                            <div className="alert alert-danger">
                              {String(this.state.errorMsg)}
                            </div>
                          ) : null}
                          <form
                            className="form-horizontal"
                            noValidate={true}
                            onSubmit={this.handleSubmit}
                          >
                            <div className="form-group row">
                              <label className="col-sm-3 form-control-label required label">
                                Account
                              </label>
                              <div className="col-sm-9">
                                <Select
                                  name="account"
                                  isMulti={false}
                                  options={this.state.accountsList}
                                  value={this.state.accountId}
                                  onChange={this.handleAccountChange}
                                  placeholder="Select account"
                                  className="basic-multi-select fields-font"
                                  isDisabled={this.state.isEdit}
                                />
                                {this.validator.message(
                                  "accountId",
                                  this.state.accountId,
                                  "required"
                                )}
                              </div>
                            </div>
                            <div className="form-group row">
                              <label className="col-sm-3 form-control-label required label">
                                Name
                              </label>
                              <div className="col-sm-9">
                                <input
                                  className="form-control form-control-success fields-font"
                                  name="name"
                                  value={this.state.name}
                                  onChange={this.handleChange}
                                  maxLength={250}
                                  placeholder="Class Name"
                                />
                                {this.validator.message(
                                  "name",
                                  this.state.name,
                                  "required|max:250",
                                  {
                                    messages: {
                                      required: "Field is required!",
                                      max: "Name cannot exceed 250 characters.",
                                    },
                                  }
                                )}
                              </div>
                            </div>
                            <div className="form-group row">
                              <label className="col-sm-3 form-control-label label">
                                Description
                              </label>
                              <div className="col-sm-9">
                                <textarea
                                  className="form-control form-control-success fields-font"
                                  name="description"
                                  value={this.state.description}
                                  onChange={this.handleChange}
                                  rows={3}
                                  maxLength={5000}
                                  placeholder="Description"
                                />
                              </div>
                            </div>
                            <div className="form-group row">
                              <label className="col-sm-3 form-control-label label">
                                Comments
                              </label>
                              <div className="col-sm-9">
                                <textarea
                                  className="form-control form-control-success fields-font"
                                  name="comments"
                                  value={this.state.comments}
                                  onChange={this.handleChange}
                                  rows={2}
                                  maxLength={5000}
                                  placeholder="Comments"
                                />
                              </div>
                            </div>
                            <div className="form-group row">
                              <label className="col-sm-3 form-control-label required label">
                                Vendors
                              </label>
                              <div className="col-sm-9">
                                <Select
                                  name="vendors"
                                  isMulti={true}
                                  options={this.getVendorOptions()}
                                  value={this.state.vendors}
                                  onChange={this.handleVendorChange}
                                  placeholder="Select Student Pay alone, or VitalSource and/or Follett"
                                  className="basic-multi-select fields-font"
                                />
                                {this.validator.message(
                                  "vendors",
                                  this.state.vendors,
                                  "required"
                                )}
                                <small className="form-text text-muted">
                                  Student Pay cannot be combined with bookstore
                                  vendors.
                                </small>
                              </div>
                            </div>
                            <div className="form-group row">
                              <label className="col-sm-3 form-control-label required label">
                                Simulations
                              </label>
                              <div className="col-sm-9">
                                <Select
                                  name="simulations"
                                  isMulti={true}
                                  options={this.getSimulationOptions()}
                                  value={this.state.simulationIds}
                                  onChange={this.handleSimulationChange}
                                  placeholder={
                                    this.state.accountId
                                      ? "Select simulations"
                                      : "Select an account first"
                                  }
                                  isDisabled={!this.state.accountId}
                                  className="basic-multi-select fields-font"
                                />
                                {this.validator.message(
                                  "simulationIds",
                                  this.state.simulationIds,
                                  "required"
                                )}
                              </div>
                            </div>

                            {isPay ? (
                              <>
                                <div className="form-group row">
                                  <label className="col-sm-3 form-control-label required label">
                                    Class Expiration
                                  </label>
                                  <div className="col-sm-9">
                                    <div
                                      className="form-control mb-3 fields-font"
                                      style={{
                                        padding: 0,
                                        paddingTop: 4,
                                        paddingLeft: 8,
                                      }}
                                    >
                                      <CustomDatePicker
                                        expiryDate={this.state.expiryDate}
                                        handleDateChange={(expiry) =>
                                          this.handleDateChange(expiry)
                                        }
                                      />
                                    </div>
                                    {this.state.expiryDateInvalid
                                      ? null
                                      : this.validator.message(
                                          "expiryDate",
                                          this.state.expiryDate,
                                          "required"
                                        )}
                                  </div>
                                </div>
                                <div className="form-group row">
                                  <label className="col-sm-3 form-control-label required label">
                                    Amount
                                  </label>
                                  <div className="col-sm-9">
                                    <input
                                      className="form-control form-control-success fields-font"
                                      name="paymentAmount"
                                      type="number"
                                      min="1"
                                      max="99999999.99"
                                      step="0.01"
                                      value={this.state.paymentAmount}
                                      onChange={this.handleChange}
                                      onKeyDown={(event) => {
                                        if (
                                          ["e", "E", "+", "-"].includes(
                                            event.key
                                          )
                                        ) {
                                          event.preventDefault();
                                        }
                                      }}
                                    />
                                    {this.validator.message(
                                      "paymentAmount",
                                      this.state.paymentAmount,
                                      "required|numeric|min:1,num",
                                      {
                                        messages: {
                                          required: "Field is required!",
                                          numeric:
                                            "Amount should be greater than or equal to 1.",
                                          min:
                                            "Amount should be greater than or equal to 1.",
                                        },
                                      }
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : null}

                            {isBookstore ? (
                              <>
                                <div className="form-group row">
                                  <label className="col-sm-3 form-control-label required label">
                                    Access window (days after redeem)
                                  </label>
                                  <div className="col-sm-9">
                                    <input
                                      className="form-control form-control-success fields-font"
                                      name="accessWindowDays"
                                      type="number"
                                      min="1"
                                      max="999999999"
                                      step="1"
                                      maxLength={9}
                                      value={this.state.accessWindowDays}
                                      onChange={this.handleChange}
                                      onKeyDown={vendorRules.preventNonIntegerKey}
                                    />
                                    {this.validator.message(
                                      "accessWindowDays",
                                      this.state.accessWindowDays,
                                      "required|integer|min:1,num|max:999999999,num",
                                      {
                                        messages: {
                                          required: "Field is required!",
                                          integer:
                                            "Access window should be greater than or equal to 1 days.",
                                          min:
                                            "Access window should be greater than or equal to 1 days.",
                                          max:
                                            "Access window cannot exceed 999999999 days.",
                                        },
                                      }
                                    )}
                                  </div>
                                </div>
                                {this.state.isEdit ? (
                                  <div className="form-group row">
                                    <div className="col-sm-9 offset-sm-3">
                                      <div className="border p-3 mb-3">
                                        <h5>Generate Access Codes</h5>
                                        {this.bookstoreVendorsForGenerate().map(
                                          (vendorKey) => {
                                            const inv =
                                              this.vendorInventory(vendorKey);
                                            return (
                                              <p
                                                key={vendorKey}
                                                className="mb-1"
                                              >
                                                <strong>
                                                  {this.vendorLabel(vendorKey)}
                                                </strong>
                                                {" — "}
                                                Available:{" "}
                                                <span
                                                  style={{
                                                    color:
                                                      Number(inv.available) <=
                                                      10
                                                        ? "#c62828"
                                                        : undefined,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {inv.available}
                                                </span>{" "}
                                                (redeemed: {inv.redeemed},
                                                expired: {inv.expired},
                                                revoked: {inv.revoked})
                                              </p>
                                            );
                                          }
                                        )}
                                        {this.unsavedBookstoreVendorKeys()
                                          .length > 0 ? (
                                          <p className="text-muted small mb-2">
                                            Save the class to enable Generate
                                            for{" "}
                                            {this.unsavedBookstoreVendorKeys()
                                              .map((k) => this.vendorLabel(k))
                                              .join(", ")}
                                            .
                                          </p>
                                        ) : null}
                                        {this.savedBookstoreVendorKeys()
                                          .length === 0 ? (
                                          <p className="text-muted small mb-2">
                                            Save at least one bookstore vendor
                                            (VitalSource and/or Follett) before
                                            generating access codes.
                                          </p>
                                        ) : null}
                                        <div className="mt-2">
                                          <button
                                            type="button"
                                            className="btn btn-secondary mr-2 mb-2"
                                            disabled={
                                              this.bookstoreVendorsForGenerate()
                                                .length === 0
                                            }
                                            onClick={this.openGenerateModal}
                                          >
                                            Generate
                                          </button>
                                          {this.hasBookstoreCodes() ? (
                                            <button
                                              type="button"
                                              className="btn btn-outline-secondary mb-2"
                                              onClick={this.downloadAllCodes}
                                            >
                                              Download all generated codes
                                            </button>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="form-group row">
                                    <div className="col-sm-9 offset-sm-3">
                                      <p className="text-muted">
                                        Save the class first, then generate
                                        access codes (an Excel file downloads
                                        automatically).
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : null}

                            <hr />
                            <div className="form-group row">
                              <div className="col-sm-9 offset-sm-3">
                                <h4>Landing Page</h4>
                              </div>
                            </div>
                            <div className="form-group row">
                              <label className="col-sm-3 form-control-label label">
                                Landing Page Instructions
                              </label>
                              <div className="col-sm-9 email-editor fields-font">
                                {/* Same editor as the message centre, so pictures and videos are
                                    available here with its size/format validation. */}
                                {this.state.editorReady ? (
                                  <TipTapEditor
                                    content={this.state.landingInstructions}
                                    onChange={(value) =>
                                      this.setState({
                                        landingInstructions: value,
                                      })
                                    }
                                  />
                                ) : null}
                              </div>
                            </div>
                            {this.state.isEdit && this.state.landingUrl ? (
                              <div className="form-group row">
                                <label
                                  className="col-sm-3 form-control-label label"
                                  htmlFor="ss-landing-page-url"
                                >
                                  Landing Page URL
                                </label>
                                <div className="col-sm-9">
                                  <div className="input-group">
                                    <input
                                      id="ss-landing-page-url"
                                      className="form-control fields-font"
                                      value={this.state.landingUrl}
                                      readOnly
                                    />
                                    <div className="input-group-append">
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={this.copyLandingUrl}
                                      >
                                        Copy
                                      </button>
                                    </div>
                                  </div>
                                  {this.state.copyFeedback ? (
                                    <small className="form-text text-success">
                                      {this.state.copyFeedback}
                                    </small>
                                  ) : null}
                                </div>
                              </div>
                            ) : (
                              <div className="form-group row">
                                <div className="col-sm-9 offset-sm-3">
                                  <p className="text-muted">
                                    A unique landing URL is generated
                                    automatically when you save.
                                  </p>
                                </div>
                              </div>
                            )}
                            <div className="form-group row">
                              <div className="col-sm-9 offset-sm-3">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="landingOptionPay"
                                    title="Students pay by card to enroll (Student Pay only)."
                                    name="landingOptionPay"
                                    checked={this.state.landingOptionPay}
                                    onChange={this.handleChange}
                                    disabled={!isPay}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="landingOptionPay"
                                    title="Students pay by card to enroll (Student Pay only)."
                                  >
                                    Register and pay
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="form-group row">
                              <div className="col-sm-9 offset-sm-3">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="landingOptionEnterCode"
                                    title="Students enter the code from their bookstore or confirmation email."
                                    name="landingOptionEnterCode"
                                    checked={this.state.landingOptionEnterCode}
                                    onChange={this.handleChange}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="landingOptionEnterCode"
                                    title="Students enter the code from their bookstore or confirmation email."
                                  >
                                    Enter access code
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="form-group row">
                              <div className="col-sm-9 offset-sm-3">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="landingOptionForgotCode"
                                    title="Students can get their code re-sent by email."
                                    name="landingOptionForgotCode"
                                    checked={this.state.landingOptionForgotCode}
                                    onChange={this.handleChange}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="landingOptionForgotCode"
                                    title="Students can get their code re-sent by email."
                                  >
                                    Forgot your code?
                                  </label>
                                </div>
                              </div>
                            </div>
                            {this.state.isEdit ? (
                            <div className="form-group row">
                              <div className="col-sm-3 form-control-label"></div>
                              <div className="col-sm-9">
                                <div className="custom-control custom-checkbox checkbox-xl">
                                  <input
                                    id="active"
                                    type="checkbox"
                                    checked={!!this.state.active}
                                    name="active"
                                    className="custom-control-input"
                                    // Status follows the expiry date; admins cannot toggle it.
                                    disabled={true}
                                    readOnly
                                  />
                                  <label
                                    className="custom-control-label"
                                    htmlFor="active"
                                  >
                                    Active
                                  </label>
                                </div>
                              </div>
                            </div>
                            ) : null}

                            <div className="form-group row">
                              <div className="col-sm-9 offset-sm-3">
                                <button
                                  type="submit"
                                  className="btn btn-submit float-right buttons"
                                >
                                  {this.state.isEdit ? "Update Class" : "Create Class"}
                                </button>
                                <Link
                                  to="/classes"
                                  className="btn btn-cancel float-right buttons"
                                >
                                  Cancel
                                </Link>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <DefaultFooter />
              {this.state.loading ? (
                <div className="overlay">
                  <Loader loading={this.state.loading} />
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <Dialog
          open={!!this.state.showVendorWarningModal}
          onClose={this.closeVendorWarningModal}
          onExited={this.clearVendorWarning}
          aria-labelledby="vendor-warning-dialog-title"
          aria-describedby="vendor-warning-dialog-description"
        >
          <DialogTitle id="vendor-warning-dialog-title">Warning</DialogTitle>
          <DialogContent>
            <DialogContentText id="vendor-warning-dialog-description">
              {this.state.vendorWarningMessage}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {this.state.vendorSelectionUndo ? (
              <Button onClick={this.cancelVendorChange} color="primary">
                Cancel
              </Button>
            ) : null}
            <Button
              onClick={this.acceptVendorWarning}
              className="dialog-button"
              color="primary"
              variant="contained"
            >
              {this.state.vendorSelectionUndo ? "Continue" : "Ok"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={!!this.state.showGenerateModal}
          onClose={this.closeGenerateModal}
          aria-labelledby="generate-access-codes-title"
          fullWidth
          maxWidth="xs"
          PaperProps={{ style: { overflow: "visible" } }}
        >
          <DialogTitle id="generate-access-codes-title">
            Generate Access Codes
          </DialogTitle>
          <DialogContent style={{ overflow: "visible" }}>
            <div className="form-group">
              <label className="form-control-label required label">
                Vendors
              </label>
              <Select
                name="generateVendors"
                aria-label="Vendors"
                isMulti={true}
                options={this.bookstoreVendorsForGenerateOptions()}
                value={this.state.generateModalVendors}
                onChange={this.handleGenerateVendorsChange}
                placeholder="Select vendors"
                className="basic-multi-select fields-font"
              />
              {this.state.generateModalVendorError ? (
                <div className="srv-validation-message">
                  {this.state.generateModalVendorError}
                </div>
              ) : null}
            </div>
            {(this.state.generateModalVendors || []).map((option) => (
              <div className="form-group mb-2" key={option.value}>
                <label
                  className="form-control-label required label"
                  htmlFor={`generate-quantity-${option.value}`}
                >
                  {option.label} Quantity
                </label>
                <input
                  id={`generate-quantity-${option.value}`}
                  className="form-control"
                  type="number"
                  min="1"
                  max={vendorRules.MAX_GENERATE_QUANTITY}
                  step="1"
                  value={this.state.generateModalQuantities[option.value] ?? ""}
                  onKeyDown={vendorRules.preventNonIntegerKey}
                  onChange={(e) =>
                    this.handleGenerateQuantityChange(option.value, e.target.value)
                  }
                />
                {this.state.generateModalQuantityErrors[option.value] ? (
                  <div className="srv-validation-message">
                    {this.state.generateModalQuantityErrors[option.value]}
                  </div>
                ) : null}
              </div>
            ))}
            {this.state.generateModalError ? (
              <div className="srv-validation-message mt-2">
                {this.state.generateModalError}
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeGenerateModal} color="primary">
              Cancel
            </Button>
            <Button
              onClick={this.confirmGenerateFromModal}
              className="dialog-button"
              color="primary"
              variant="contained"
            >
              Generate
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default AddSelfServeClass;
